import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

interface AdminContextType {
  isAdmin: boolean;
  user: { id: string; email: string; displayName: string | null } | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, displayName: string, gender?: string, source?: string, nationality?: string, birth?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  user: null,
  isLoading: true,
  login: async () => ({ success: false }),
  signup: async () => ({ success: false }),
  logout: async () => {},
});

export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AdminContextType['user']>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await loadUserProfile(session.user.id, session.user.email || '');
      }
      setIsLoading(false);
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadUserProfile(session.user.id, session.user.email || '');
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });

    return () => { subscription.unsubscribe(); };
  }, []);

  const loadUserProfile = async (userId: string, email: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin, display_name')
      .eq('id', userId)
      .maybeSingle();

    if (!error && data) {
      setUser({ id: userId, email, displayName: data.display_name });
      setIsAdmin(!!data.is_admin);
    } else {
      setUser({ id: userId, email, displayName: null });
      setIsAdmin(false);
    }
  };

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    if (data.user) {
      await loadUserProfile(data.user.id, data.user.email || '');
      return { success: true };
    }
    return { success: false, error: 'Login failed' };
  };

  const signup = async (email: string, password: string, displayName: string, gender?: string, source?: string, nationality?: string, birth?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } },
    });
    if (error) return { success: false, error: error.message };
    if (data.user) {
// const profileData: any = {
//   id: data.user.id,
//   display_name: displayName,
//   email: data.user.email,
//   is_admin: false,
// };
// if (gender) profileData.gender = gender;
// if (source) profileData.source = source;
// if (nationality) profileData.nationality = nationality;
// if (birth) profileData.birth = birth;

// const { error: insertError } = await supabase.from('profiles').insert(profileData);
// if (insertError) {
//   console.error('Error inserting profile:', insertError);
//   return { success: false, error: `Profile creation failed: ${insertError.message}` };
// }

      await loadUserProfile(data.user.id, data.user.email || '');
      return { success: true };
    }
    return { success: false, error: 'Signup failed' };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
  };

  return (
    <AdminContext.Provider value={{ isAdmin, user, isLoading, login, signup, logout }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);